import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { stats } from '../mockData';

const CountUp = ({ end, suffix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = (timestamp - startTime) / (duration * 1000);

      if (progress < 1) {
        setCount(end * progress);
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isInView, end, duration]);

  return (
    <span ref={ref}>
      {end % 1 === 0 ? Math.floor(count) : count.toFixed(1)}
      {suffix}
    </span>
  );
};

const About = () => {
  return (
    <section id="about" className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-blue-950/20 to-slate-950"></div>
      
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `linear-gradient(rgba(0, 245, 255, 0.1) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(0, 245, 255, 0.1) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }}></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-6 px-4"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">
              About ICD
            </h2>
            <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-cyan-500 to-blue-600 neon-glow"></div>
            
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 leading-relaxed">
              We are <span className="text-cyan-400 font-bold">ICD – Innovative Custom Designs</span>. 
              We don't just create visuals. We architect brands that grow, connect, and dominate in the digital future.
            </p>
            
            <p className="text-sm sm:text-base lg:text-lg text-gray-400 leading-relaxed">
              Whether you're launching a new venture or scaling an existing empire, our futuristic approach delivers measurable results. 
              We combine cutting-edge design with strategic thinking to position your brand as the industry leader.
            </p>

            <motion.div
              whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(0,245,255,0.6)' }}
              whileTap={{ scale: 0.95 }}
              className="inline-block"
            >
              <button className="mt-4 sm:mt-6 px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-base sm:text-lg font-bold rounded-lg neon-glow">
                Learn Our Story
              </button>
            </motion.div>
          </motion.div>

          {/* Right: Stats */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ scale: 1.05 }}
                className="relative group"
              >
                <div className="p-6 sm:p-8 bg-gradient-to-br from-slate-900/80 to-slate-800/80 rounded-2xl border border-cyan-500/20 hover:border-cyan-500/60 transition-all duration-300 backdrop-blur-sm">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-purple-600/0 group-hover:from-cyan-500/10 group-hover:to-purple-600/10 rounded-2xl transition-all duration-300"></div>
                  
                  <div className="relative">
                    <div className="text-4xl sm:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-2 sm:mb-3">
                      <CountUp end={stat.value} suffix={stat.suffix} />
                      {stat.suffix === '+' && '+'}
                    </div>
                    <div className="text-gray-400 text-sm sm:text-base lg:text-lg font-semibold uppercase tracking-wide">
                      {stat.label}
                    </div>
                  </div>

                  {/* Corner decoration */}
                  <div className="absolute bottom-0 left-0 w-16 h-16 border-l-2 border-b-2 border-cyan-500/30 rounded-bl-xl"></div>
                  <div className="absolute top-0 right-0 w-16 h-16 border-r-2 border-t-2 border-purple-500/30 rounded-tr-xl"></div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
